import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { getBezierPath, EdgeProps } from '@xyflow/react'

interface HandleElement {
  id: string
  position: string
}

interface CustomEdgeData {
  sourceHandle?: HandleElement & { style?: React.CSSProperties }
  targetHandle?: HandleElement & { style?: React.CSSProperties }
  onDelete?: (id: string) => void
  bundleCount?: number
  signalType?: string
  // Optional display labels for the hover tooltip. Populated at
  // edge-projection time in EditorClient. When absent the tooltip
  // falls back to a signalType-only description.
  sourceDeviceName?: string
  sourcePortName?: string
  targetDeviceName?: string
  targetPortName?: string
}

type CustomEdgeProps = EdgeProps & {
  data?: CustomEdgeData
}

const ConnectionEdge: React.FC<CustomEdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  data,
  selected,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  // Mouse position in viewport coordinates — used to anchor the
  // hover tooltip via a React portal into document.body, so the
  // tooltip escapes React Flow's SVG layer (which is always below
  // the nodes DOM layer). Without this, tooltips render behind
  // nearby device cards on busy canvases. Updating on every
  // mousemove lets the tooltip track the cursor naturally.
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(
    null,
  )

  // Bezier path — gives the smooth cable-like curves the user
  // wants across every view. Right-angle getSmoothStepPath was
  // correct for orthogonal routing but read as a schematic, not a
  // patch bay. curvature=0.4 is a good middle ground between
  // taut and droopy.
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.4,
  })

  // Prefer explicit style.stroke when the edge factory sets it
  // (every real edge does). Fall back to the handle-style path for
  // legacy callers, then grey. Without this fallback every drilldown
  // edge rendered as a uniform grey because the edge factory only
  // sets style.stroke, not data.sourceHandle.style.background.
  const color =
    (style && typeof style === 'object' && 'stroke' in style
      ? (style as React.CSSProperties).stroke
      : undefined) ||
    data?.sourceHandle?.style?.background ||
    data?.targetHandle?.style?.background ||
    '#999'

  // Determine stroke dash pattern based on signal type
  const getStrokeDashArray = () => {
    switch (data?.signalType) {
      case 'midi':
        return '8 4'
      case 'cv_gate':
        return '4 4'
      case 'sync':
        return '2 4'
      default:
        return 'none'
    }
  }

  // Calculate stroke width based on bundle count
  const getStrokeWidth = () => {
    if (!data?.bundleCount || data.bundleCount <= 1) return 2
    if (data.bundleCount <= 4) return 3
    return 4
  }

  const strokeDashArray = getStrokeDashArray()
  const strokeWidth = getStrokeWidth()

  return (
    <>
      {/* Invisible hit area for easier mouse targeting */}
      <path
        d={edgePath}
        stroke="transparent"
        strokeWidth={12}
        fill="none"
        pointerEvents="auto"
        onMouseEnter={e => {
          setIsHovered(true)
          setMousePos({ x: e.clientX, y: e.clientY })
        }}
        onMouseMove={e => setMousePos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => {
          setIsHovered(false)
          setMousePos(null)
        }}
      />

      {/* Main edge path with styling */}
      <path
        id={id}
        style={{
          ...style,
          stroke: String(color),
          strokeWidth,
          strokeDasharray: strokeDashArray,
          zIndex: 1000,
          opacity: isHovered || selected ? 1 : 0.7,
          transition: 'opacity 0.2s ease',
        }}
        className="react-flow__edge-path"
        d={edgePath}
        onMouseEnter={e => {
          setIsHovered(true)
          setMousePos({ x: e.clientX, y: e.clientY })
        }}
        onMouseMove={e => setMousePos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => {
          setIsHovered(false)
          setMousePos(null)
        }}
      />

      {/* Hover tooltip — rendered via portal into document.body to
          escape the SVG edges layer, which is always below the nodes
          DOM layer. The tooltip follows the cursor with an offset so
          it never covers the edge itself, and pointer-events: none
          means hover enters/leaves stay bound to the hit-area path
          below. */}
      {isHovered &&
        mousePos &&
        (data?.sourceDeviceName || data?.targetDeviceName) &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: mousePos.y + 12,
              left: mousePos.x + 12,
              pointerEvents: 'none',
              zIndex: 100000,
              fontFamily: 'inherit',
            }}
          >
            <div
              className="rounded-lg px-2.5 py-1.5 shadow-2xl border whitespace-nowrap"
              style={{
                backgroundColor: 'rgba(15, 23, 42, 0.96)',
                borderColor: String(color),
                color: 'white',
                borderLeftWidth: 3,
                maxWidth: '320px',
              }}
            >
              <div className="flex items-center gap-1.5 text-[10px] font-semibold">
                <span className="text-gray-400 uppercase tracking-wider">
                  {data?.sourceDeviceName ?? 'Source'}
                </span>
                <span className="truncate">{data?.sourcePortName ?? ''}</span>
              </div>
              <div
                className="text-[9px] my-0.5 tracking-widest"
                style={{ color: String(color) }}
              >
                ↓ {data?.signalType ?? 'signal'}
              </div>
              <div className="flex items-center gap-1.5 text-[10px] font-semibold">
                <span className="text-gray-400 uppercase tracking-wider">
                  {data?.targetDeviceName ?? 'Target'}
                </span>
                <span className="truncate">{data?.targetPortName ?? ''}</span>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Bundle count badge */}
      {data?.bundleCount && data.bundleCount > 1 && (
        <foreignObject
          width={36}
          height={16}
          x={(sourceX + targetX) / 2 - 18}
          y={(sourceY + targetY) / 2 - 8}
          className="overflow-visible pointer-events-none"
          requiredExtensions="http://www.w3.org/1999/xhtml"
          style={{ zIndex: 1001 }}
        >
          <div className="flex justify-center items-center w-full h-full">
            <span
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap"
              style={{
                backgroundColor: 'white',
                color: String(color),
                border: `1px solid ${String(color)}`,
              }}
            >
              [{data.bundleCount}x]
            </span>
          </div>
        </foreignObject>
      )}

      {/* Delete button */}
      <foreignObject
        width={20}
        height={20}
        x={
          (sourceX + targetX) / 2 +
          (data?.bundleCount && data.bundleCount > 1 ? 20 : 10) -
          10
        }
        y={(sourceY + targetY) / 2 - 10}
        className="overflow-visible pointer-events-none"
        requiredExtensions="http://www.w3.org/1999/xhtml"
        style={{ zIndex: 1002 }}
      >
        <div className="flex justify-center items-center w-full h-full">
          {data?.onDelete && (isHovered || selected) && (
            <button
              className="w-3 h-3 bg-red-500 border border-white text-white rounded-full edge-button leading-none cursor-pointer pointer-events-auto transition-all duration-200 ease-in-out hover:bg-red-600 hover:scale-110 shadow-md flex justify-center items-center text-[8px]"
              style={{ zIndex: 1002 }}
              onClick={event => {
                event.stopPropagation()
                data.onDelete!(id)
              }}
            >
              X
            </button>
          )}
        </div>
      </foreignObject>
    </>
  )
}

export default ConnectionEdge
